import { ReactNode, useId } from 'react';

import Button from 'components/ui/button';
import { ModalFrame, ModalFrameSize } from 'components/ui/modal-frame';

import FeedbackImg from 'assets/img/comportamento/bg-feedback-moedas.png';

/** Nome acessível do fechar dos modais de ação, o mesmo do X da identidade antiga. */
const ROTULO_DO_FECHAR = 'Fechar modal';

// SPEC_DEVIATION: o CTA do design é amarelo sobre `brand-blue`; vai no `solido-destaque` (azul `button-accent`).
// Reason: texto amarelo pequeno (12–14px) pede 4,5:1 (WCAG 1.4.3); a variante é a resposta já usada no repo.
const VARIANTE_DO_CTA = 'solido-destaque';

/** Campo `.jj-field` do design: borda cinza, azul no foco. */
export const CAMPO_DA_ACAO =
  'w-full resize-none rounded-jj-field border border-content-soft bg-brand-white px-3.5 py-[11px] font-text text-base leading-normal text-content focus:border-brand-blue focus:outline-none sm:text-[13px]';

interface ConfirmacaoDaAcaoProps {
  isOpen?: boolean;
  title: string;
  /** Linha cinza sob o título (o nome da realização). */
  subtitle?: ReactNode;
  /** Ícone sobre o título; centraliza cabeçalho e rodapé. */
  illustration?: ReactNode;
  /** O texto do modal; vira a descrição acessível quando não há subtítulo. */
  children?: ReactNode;
  /** X, Esc e "Voltar": o mesmo caminho. */
  onClose: () => void;
  /** Nome acessível do X. Padrão "Fechar modal". */
  closeLabel?: string;
  onConfirm: () => void;
  confirmLabel?: string;
  confirmDisabled?: boolean;
  /** Trava o "Voltar" enquanto a gravação está em andamento. */
  backDisabled?: boolean;
  /** Texto do botão de voltar. Padrão "Voltar". */
  backLabel?: string;
  /** Tira o X e o Esc enquanto a gravação está em andamento. */
  closeDisabled?: boolean;
  size?: ModalFrameSize;
}

/**
 * Modal de ação do cartão na nova identidade (design de `Realizacao Destaque.dc.html`): cartão com borda rosa,
 * título, texto, e o rodapé com "Voltar" e a ação principal.
 */
export function ConfirmacaoDaAcao({
  isOpen = true,
  title,
  subtitle,
  illustration,
  children,
  onClose,
  closeLabel = ROTULO_DO_FECHAR,
  onConfirm,
  confirmLabel = 'Confirmar',
  confirmDisabled = false,
  backDisabled = false,
  backLabel = 'Voltar',
  closeDisabled = false,
  size = 'sm',
}: ConfirmacaoDaAcaoProps) {
  const textoId = useId();

  return (
    <ModalFrame
      isOpen={isOpen}
      appearance="cartao"
      size={size}
      title={title}
      subtitle={subtitle}
      descriptionId={children ? textoId : undefined}
      illustration={illustration}
      onClose={closeDisabled ? undefined : onClose}
      closeLabel={closeLabel}
      footer={
        <>
          <Button variant="secundario" className={illustration ? '' : 'ml-auto'} onClick={onClose} disabled={backDisabled}>
            {backLabel}
          </Button>
          <Button variant={VARIANTE_DO_CTA} onClick={onConfirm} disabled={confirmDisabled}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {/* D3: 30px e 12,5px não têm degrau no Tailwind. */}
      {children && (
        <div
          id={textoId}
          className={`flex flex-col gap-4 px-5 pt-2 text-[12.5px] leading-[1.65] text-content [text-wrap:pretty] lg:px-[30px] ${
            illustration ? 'text-center' : ''
          }`}
        >
          {children}
        </div>
      )}
    </ModalFrame>
  );
}

interface ResultadoDaAcaoProps {
  title: string;
  /** O texto do resultado; anunciado como status. */
  children: ReactNode;
  /** X, Esc e "Fechar": o mesmo caminho. */
  onClose: () => void;
  /** Sem a ilustração das moedas (resultado de falha); o cabeçalho e o rodapé deixam de ser centralizados. */
  semIlustracao?: boolean;
}

/** Tela de resultado ("Mandou bem!") no desenho de `Minha Equipe.dc.html`: ilustração, título, texto e "Fechar". */
export function ResultadoDaAcao({ title, children, onClose, semIlustracao = false }: ResultadoDaAcaoProps) {
  const textoId = useId();

  return (
    <ModalFrame
      isOpen
      appearance="cartao"
      size="sm"
      title={title}
      descriptionId={textoId}
      illustration={semIlustracao ? undefined : <img src={FeedbackImg} alt="" className="block w-[120px]" />}
      onClose={onClose}
      closeLabel={ROTULO_DO_FECHAR}
      footer={
        <Button variant={VARIANTE_DO_CTA} className={semIlustracao ? 'ml-auto' : ''} onClick={onClose}>
          Fechar
        </Button>
      }
    >
      <p
        id={textoId}
        role="status"
        className={`m-0 px-5 pt-2 text-[13px] leading-[1.6] text-content-soft [text-wrap:pretty] lg:px-[30px] ${
          semIlustracao ? '' : 'text-center'
        }`}
      >
        {children}
      </p>
    </ModalFrame>
  );
}
